import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Plus, X, Library, Trash2 } from "lucide-react";
import { toast } from "sonner";

export interface OptionalExtra {
  description: string;
  price_per_person: number;
  save_to_library?: boolean;
  library_id?: string | null;
}

interface LibraryExtra {
  id: string;
  description: string;
  price_per_person: number;
}

export function useOptionalExtrasLibrary(enabled = true) {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ["quote-optional-extras-library"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("quote_optional_extras")
        .select("id, description, price_per_person")
        .order("description");
      if (error) throw error;
      return (data || []) as LibraryExtra[];
    },
    enabled,
  });

  const addToLibrary = useMutation({
    mutationFn: async (extra: { description: string; price_per_person: number }) => {
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase.from("quote_optional_extras").insert({
        description: extra.description,
        price_per_person: extra.price_per_person,
        created_by: user?.id ?? null,
      } as any);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["quote-optional-extras-library"] });
    },
  });

  const removeFromLibrary = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("quote_optional_extras").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["quote-optional-extras-library"] });
      toast.success("Removed from library");
    },
    onError: () => toast.error("Could not remove that entry"),
  });

  return { library: query.data ?? [], isLoading: query.isLoading, isError: query.isError, error: query.error as Error | null, addToLibrary, removeFromLibrary };
}

interface OptionalExtrasSectionProps {
  extras: OptionalExtra[];
  onChange: (extras: OptionalExtra[]) => void;
  currencySymbol: string;
}

export function OptionalExtrasSection({ extras, onChange, currencySymbol }: OptionalExtrasSectionProps) {
  const { library, isLoading, isError, error, removeFromLibrary } = useOptionalExtrasLibrary();
  const [manageOpen, setManageOpen] = useState(false);

  const displayLibrary = useMemo(() => {
    const seen = new Set<string>();
    return library.filter((item) => {
      const key = `${(item.description || "").trim().toLowerCase()}|${Number(item.price_per_person) || 0}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [library]);

  const addBlank = () => onChange([...extras, { description: "", price_per_person: 0 }]);

  const addFromLibrary = (item: LibraryExtra) =>
    onChange([
      ...extras,
      {
        description: item.description,
        price_per_person: Number(item.price_per_person) || 0,
        library_id: item.id,
      },
    ]);

  const update = (index: number, updates: Partial<OptionalExtra>) =>
    onChange(extras.map((e, i) => (i === index ? { ...e, ...updates } : e)));

  const remove = (index: number) => onChange(extras.filter((_, i) => i !== index));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <Label className="text-sm font-medium">Optional Extras</Label>
          <p className="text-xs text-muted-foreground">
            Shown separately on the quote, per person. Not added to the total.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button type="button" variant="outline" size="sm" className="gap-1 h-7 text-xs">
                <Library className="h-3 w-3" />
                Library
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-72 max-h-64 overflow-y-auto"
              disablePortal
            >
              <DropdownMenuLabel>Saved extras</DropdownMenuLabel>
              {isLoading && (
                <div className="px-2 py-1.5 text-xs text-muted-foreground">
                  Loading saved extras...
                </div>
              )}
              {isError && (
                <div className="px-2 py-1.5 text-xs text-destructive">
                  Could not load extras: {error?.message || "Unknown error"}
                </div>
              )}
                {!isLoading && !isError && displayLibrary.length === 0 && (
                  <div className="px-2 py-1.5 text-xs text-muted-foreground">
                    Nothing saved yet — tick "Save to library" on a line.
                  </div>
                )}
              {!isLoading &&
                !isError &&
                displayLibrary.map((item) => (
                  <DropdownMenuItem key={item.id} onSelect={() => addFromLibrary(item)}>
                    <span className="flex-1 truncate">{item.description}</span>
                    <span className="ml-2 text-xs text-muted-foreground">
                      {currencySymbol}
                      {Number(item.price_per_person).toLocaleString()}
                    </span>
                  </DropdownMenuItem>
                ))}
              {displayLibrary.length > 0 && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setManageOpen((v) => !v); }}>
                    {manageOpen ? "Hide manage list" : "Manage library"}
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button type="button" variant="outline" size="sm" onClick={addBlank} className="gap-1 h-7 text-xs">
            <Plus className="h-3 w-3" />
            Add Extra
          </Button>
        </div>
      </div>

      {manageOpen && displayLibrary.length > 0 && (
        <div className="rounded-md border p-2 space-y-1">
          {displayLibrary.map((item) => (
            <div key={item.id} className="flex items-center gap-2 text-xs">
              <span className="flex-1 truncate">{item.description}</span>
              <span className="text-muted-foreground">
                {currencySymbol}
                {Number(item.price_per_person).toLocaleString()}
              </span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => removeFromLibrary.mutate(item.id)}
              >
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {extras.map((extra, index) => (
        <div key={index} className="space-y-2 rounded-md border p-2">
          <div className="flex items-center gap-2">
            <Input
              className="h-9 flex-1"
              placeholder="Description (e.g. Upgrade Table Mountain tickets to Fast Track)"
              value={extra.description}
              onChange={(e) => update(index, { description: e.target.value })}
            />
            <div className="flex items-center gap-1">
              <span className="text-sm text-muted-foreground">{currencySymbol}</span>
              <Input
                type="number"
                step="0.01"
                min="0"
                className="h-9 w-28"
                placeholder="0.00"
                value={extra.price_per_person || ""}
                onChange={(e) =>
                  update(index, { price_per_person: parseFloat(e.target.value) || 0 })
                }
              />
              <span className="text-xs text-muted-foreground whitespace-nowrap">pp</span>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={() => remove(index)}
            >
              <X className="h-3 w-3" />
            </Button>
          </div>
          {!extra.library_id && (
            <label className="flex items-center gap-2 text-xs text-muted-foreground">
              <Checkbox
                checked={!!extra.save_to_library}
                onCheckedChange={(v) => update(index, { save_to_library: v === true })}
              />
              Save to library for future quotes
            </label>
          )}
        </div>
      ))}
    </div>
  );
}

export function serializeOptionalExtras(extras: OptionalExtra[]) {
  const cleaned = extras
    .map((e) => ({
      description: (e.description || "").trim(),
      price_per_person: Number(e.price_per_person) || 0,
    }))
    .filter((e) => e.description.length > 0 && e.price_per_person > 0);
  return cleaned.length > 0 ? cleaned : null;
}

/**
 * Persists any lines the user ticked "Save to library" on. Failures are
 * non-blocking — quote generation must never fail because of the library.
 */
export async function persistNewLibraryExtras(extras: OptionalExtra[]) {
  const toSave = extras.filter(
    (e) => e.save_to_library && !e.library_id && (e.description || "").trim() && Number(e.price_per_person) > 0
  );
  if (toSave.length === 0) return;
  try {
    const { data: { user } } = await supabase.auth.getUser();
    await supabase.from("quote_optional_extras").insert(
      toSave.map((e) => ({
        description: e.description.trim(),
        price_per_person: Number(e.price_per_person),
        created_by: user?.id ?? null,
      })) as any
    );
  } catch (err) {
    console.error("Failed saving optional extras to library", err);
  }
}
